import apiFetch from '@scale/frontend-lib/api-fetch';
import { useMutation, useQuery } from '@tanstack/react-query-v4';
import {
  UpdateAttemptFeedbackRequest,
  UpdateAttemptFeedbackResponse,
  FetchAttemptFeedbackRequest,
  FetchAttemptFeedbackResponse,
  FetchDisputesEnabledResponse,
  FetchDisputesEnabledRequest,
} from '@scale/shared/types/chat';

export const FEEDBACK_API_PATH_PREFIX = 'internal/scaler/feedback/attempt';

/**
 * Invalidates the feedback center query when the mutation is successful.
 *
 * @param feedbackAttemptId The ID of the feedback attempt to mark as acknowledged.
 * @returns A mutation function and a mutation result object.
 */
export function useMarkFeedbackAsAcknowledged() {
  return useMutation(({ feedbackAttemptId }: { feedbackAttemptId: string }) => {
    return apiFetch(null, `${FEEDBACK_API_PATH_PREFIX}/acknowledgement/${feedbackAttemptId}`, {
      method: 'POST',
      body: { feedbackAcknowledged: true },
    });
  });
}

/**
 * Updates the attempt feedback for a task attempt. Specifically, it adds a dispute to the attempt feedback.
 */
export function useUpdateAttemptFeedback() {
  return useMutation((params: UpdateAttemptFeedbackRequest) =>
    apiFetch<UpdateAttemptFeedbackResponse>(null, `internal/genai/updateAttemptFeedback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    }),
  );
}

/**
 * Fetches the attempt feedback for a task attempt.
 */
export function useFetchAttemptFeedback(params: FetchAttemptFeedbackRequest) {
  return useQuery<FetchAttemptFeedbackResponse>(
    ['outlier-feedback-center', 'attempt-feedback', params.assignmentId],
    () =>
      apiFetch<FetchAttemptFeedbackResponse>(null, `internal/genai/fetchAttemptFeedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params),
      }),
    {
      enabled: !!params.assignmentId,
      refetchOnWindowFocus: false,
    },
  );
}

export function useGetDisputesEnabled(params: FetchDisputesEnabledRequest) {
  return useQuery<FetchDisputesEnabledResponse>(
    ['outlier-feedback-center', 'disputes-enabled'],
    () =>
      apiFetch<FetchDisputesEnabledResponse>(null, `internal/genai/disputes-enabled`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params),
      }),
    {
      refetchOnWindowFocus: false,
    },
  );
}
